import type { Assortment, AssortmentRich } from '@/server/assortments/assortments';
import { capitalizeFirstLetter } from './text';
import { type DistributionProfile, PurchaseType, type Quarter } from '@repo/db';

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
dayjs.extend(utc);
dayjs.extend(timezone);

export const productTypesMapping = {
  clothing: 'vaatteet',
  shoe: 'kengät',
  equipment: 'varusteet',
  food: 'elintarvikkeet',
  multi: '',
};

export type AssortmentLike = {
  brand?: string;
  season?: string | null;
  quarter?: string | null;
  productType?: string | null;
  additionalId?: string;
  type?: string;
  size?: string | null;
  nosType?: string | null;
};

export function getAssortmentName(assortment: AssortmentLike) {
  const {
    brand = '',
    season = '',
    quarter = '',
    productType = '',
    additionalId = '',
    type = '',
    size = '',
    nosType = '',
  } = assortment;

  const brandName = capitalizeFirstLetter(brand.toLowerCase());
  const assortmentType = productTypesMapping[productType as keyof typeof productTypesMapping] || '';
  const assortmentAdditionalId = additionalId ? capitalizeFirstLetter(additionalId) : '';

  const nameParts = [season];

  if (type !== 'NOS' && quarter) {
    nameParts.push(quarter);
  }

  nameParts.push(brandName);
  nameParts.push(assortmentType);

  if (type === 'NOS') {
    nameParts.push('NOS');

    if (nosType === 'SEASONAL') {
      nameParts.push('sesonki');
    } else if (nosType === 'YEAR_ROUND') {
      nameParts.push('ympärivuotinen');
    }

    if (size) {
      if (size === 'OPEN') {
        nameParts.push('avoin valikoima');
      } else {
        nameParts.push(`${size}-koko`);
      }
    }
  }

  if (assortmentAdditionalId) {
    nameParts.push(`- ${assortmentAdditionalId}`);
  }

  return nameParts
    .map((item) => String(item).trim())
    .filter(Boolean)
    .join(' ')
    .trim();
}

export function getForcedPurchaseType(generalItemCategoryGroup: string, chain: string) {
  let purchaseType = null;
  if (generalItemCategoryGroup === 'ZBAN') {
    // "Suoratoimitus"
    // Force purchaseType SHOP
    purchaseType = PurchaseType.SHOP;
  } else {
    // "Keskusvarasto"
    if (chain === 'budgetsport') {
      // Force purchaseType CENTRAL for budgetsport
      purchaseType = PurchaseType.CENTRAL;
    }
  }

  return purchaseType;
}

export const allowedDistributionProfiles: Record<
  string,
  Record<PurchaseType, Record<string, DistributionProfile[]>>
> = {
  ZNOR: {
    // Keskusvarastotilaus
    SHOP: {
      intersport: ['ZVA', 'ZLV'],
      budgetsport: ['ZVA'],
    },
    CENTRAL: {
      intersport: ['ZVA'], // Keskusvaraston ostotilaus, vain hyllytys
      budgetsport: ['ZVA'],
    },
  },
  ZBAN: {
    // Suoratoimitus
    SHOP: {
      intersport: ['ZVA'],
      budgetsport: ['ZVA'],
    },
    CENTRAL: {
      intersport: [],
      budgetsport: [],
    },
  },
};

export function getForcedDistributionProfile(
  generalItemCategoryGroup: keyof typeof allowedDistributionProfiles | (string & {}) | null,
  purchaseType: PurchaseType | null,
  chain: 'intersport' | 'budgetsport' | null
) {
  if (!generalItemCategoryGroup || !purchaseType || !chain) {
    return null;
  }

  let distributionProfile = null;

  // If only one distribution profile is allowed, force it
  if (
    allowedDistributionProfiles?.[
      generalItemCategoryGroup as keyof typeof allowedDistributionProfiles
    ]?.[purchaseType]?.[chain]?.length === 1
  ) {
    distributionProfile =
      allowedDistributionProfiles[
        generalItemCategoryGroup as keyof typeof allowedDistributionProfiles
      ]?.[purchaseType]?.[chain]?.[0];
  }

  return distributionProfile || null;
}

export function getDistributionProfileOptions(
  assortment: Assortment | AssortmentRich,
  distributionProfiles: Array<{ value: DistributionProfile; name: string }>,
  selectedValue: DistributionProfile | null = null
) {
  // All valid options
  const validDistributionProfiles = distributionProfiles
    .map((item) => item.value)
    .filter((value) => {
      // If assortment has a distribution profile, it is the only valid option
      if (assortment.distributionProfile) {
        return value === assortment.distributionProfile;
      }

      // If assortment doesn't have a distribution profile, check if the item is in the allowed options
      const allowedDistributionProfileOptions: DistributionProfile[] =
        allowedDistributionProfiles?.[
          assortment.generalItemCategoryGroup as keyof typeof allowedDistributionProfiles
        ]?.[assortment.purchaseType][assortment.chain as 'intersport' | 'budgetsport'] ?? [];

      return (
        Array.isArray(allowedDistributionProfileOptions) &&
        allowedDistributionProfileOptions.includes(value)
      );
    });

  // Valid options plus the current selected value
  const distributionProfileOptions = distributionProfiles.filter((item) => {
    return selectedValue
      ? item.value === selectedValue || validDistributionProfiles.includes(item.value)
      : validDistributionProfiles.includes(item.value);
  });

  return { distributionProfileOptions, validDistributionProfiles };
}

export function getSeasonalArticleCategory(assortment: {
  generalItemCategoryGroup: string;
  quarter: Quarter | null;
}) {
  const seasonalArticleCategoryMap = {
    Q1: '01',
    Q2: '02',
    Q3: '03',
    Q4: '04',
  };

  let category;
  if (assortment.quarter) {
    category = seasonalArticleCategoryMap[assortment.quarter];
  }

  const seasonalArticleCategory =
    assortment.generalItemCategoryGroup === 'ZNOR'
      ? 'XX'
      : typeof category !== 'undefined'
        ? category
        : null;

  return seasonalArticleCategory;
}

export function calculateAssortmentSeasonDates(season: string): {
  startDate: Date | null;
  endDate: Date | null;
} {
  const [prefix, yearStr] = season.split('/');

  const year = Number(yearStr);
  let startDate: Date | null = null;
  let endDate: Date | null = null;

  if (!isNaN(year)) {
    switch (prefix) {
      case 'KK':
        // KK: February 1 – July 31 of the same year
        startDate = dayjs.tz(`${year}-02-01 00:00:00.000`, 'Europe/Helsinki').toDate();
        endDate = dayjs.tz(`${year}-07-31 23:59:59.999`, 'Europe/Helsinki').toDate();
        break;
      case 'ST':
        // ST: August 1 – January 31 of the following year
        startDate = dayjs.tz(`${year}-08-01 00:00:00.000`, 'Europe/Helsinki').toDate();
        endDate = dayjs.tz(`${year + 1}-01-31 23:59:59.999`, 'Europe/Helsinki').toDate();
        break;
    }
  }

  return { startDate, endDate };
}
